import { Link } from 'react-router-dom';
import { usePagedList } from '../../hooks/usePagedList.js';
import SortableTh from '../../components/ui/SortableTh.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { adminGetProducts, adminDeleteProduct } from '../../api/productsApi.js';
import { useToast } from '../../context/ToastContext.jsx';
import ProductImage from '../../components/ui/ProductImage.jsx';

const STATUS_FILTERS = [
  { value: 'all',     label: '全部' },
  { value: 'active',  label: '上架' },
  { value: 'draft',   label: '草稿' },
  { value: 'soldout', label: '售完' },
];

const STATUS_LABEL = { active: '上架', draft: '草稿', soldout: '售完' };

// 後端回傳 priceMin/priceMax（價格區間）；mock 舊資料只有 price
function formatPriceRange(p) {
  const min = p.priceMin ?? p.price;
  const max = p.priceMax ?? p.price;
  if (min == null) return '—';
  if (max != null && max !== min) {
    return `NT$${min.toLocaleString()}~${max.toLocaleString()}`;
  }
  return `NT$${min.toLocaleString()}`;
}

function AdminProductsPage() {
  const { showToast } = useToast();

  const list = usePagedList(
    p => { const { q, ...rest } = p; return adminGetProducts({ ...rest, search: q }); },
    { limit: 10, initialSort: 'createdAt,desc', initialFilters: { status: 'all' } }
  );
  const { items: products, total, totalPages, page, setPage, sort, toggleSort, filters, setFilter, q, setQ, loading } = list;

  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定刪除「${name}」？此動作無法復原。`)) return;
    try {
      await adminDeleteProduct(id);
      showToast(`已刪除：${name.slice(0, 20)}`, 'success');
      list.refetch();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>商品管理<small>PRODUCTS · {total} ITEMS</small></h1>
        <Link to="/admin/products/new" className="admin-btn primary">+ 新增商品</Link>
      </div>

      <div className="admin-card" style={{ marginBottom: 16, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <input
            placeholder="搜尋名稱或品牌…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{
              flex: 1, minWidth: 240,
              padding: '10px 14px', border: '1.5px solid var(--line)',
              borderRadius: 8, font: '600 14px Inter', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter('status', f.value)}
                style={{
                  padding: '8px 14px', borderRadius: 8,
                  border: '1.5px solid ' + (filters.status === f.value ? 'var(--navy)' : 'var(--line)'),
                  background: filters.status === f.value ? 'var(--navy)' : 'var(--white)',
                  color: filters.status === f.value ? 'var(--white)' : 'var(--ink-strong)',
                  font: '700 13px "Noto Sans TC", sans-serif',
                  cursor: 'pointer',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}></th>
              <SortableTh field="name" sort={sort} onSort={toggleSort}>商品名稱</SortableTh>
              <th style={{ width: 110 }}>品牌</th>
              <th style={{ width: 100 }}>分類</th>
              <th style={{ width: 100 }}>價格</th>
              <th style={{ width: 80 }}>庫存</th>
              <SortableTh field="status" sort={sort} onSort={toggleSort} style={{ width: 90 }}>狀態</SortableTh>
              <th style={{ width: 160 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : products.length === 0
                ? <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>沒有符合條件的商品</td></tr>
                : products.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ width: 40, height: 50, background: 'var(--ice)', borderRadius: 6, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                        <ProductImage src={p.images?.[0]} alt={p.name}
                          svgStyle={{ height: '85%' }} imgStyle={{ height: '100%', maxWidth: '100%' }} />
                      </div>
                    </td>
                    <td>
                      <div style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)', marginBottom: 2 }}>
                        {p.name.length > 36 ? p.name.slice(0, 36) + '…' : p.name}
                      </div>
                      <div style={{ font: '500 11px Inter', color: 'var(--ink-soft)' }}>{p.id}</div>
                    </td>
                    <td>{p.brand}</td>
                    <td style={{ textTransform: 'uppercase', fontSize: 12, color: 'var(--ink-soft)' }}>{p.categoryId}</td>
                    <td style={{ font: '700 14px Inter' }}>{formatPriceRange(p)}</td>
                    <td style={{ font: '700 14px Inter', color: (p.stock ?? 0) < 10 ? 'var(--red)' : 'var(--ink-strong)' }}>
                      {p.stock ?? 0}
                    </td>
                    <td><span className={`chip ${p.status}`}>{STATUS_LABEL[p.status]}</span></td>
                    <td>
                      <div className="actions">
                        <Link to={`/admin/products/${p.id}/edit`} className="admin-btn ghost sm">編輯</Link>
                        <button className="admin-btn danger sm" onClick={() => handleDelete(p.id, p.name)}>刪除</button>
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

export default AdminProductsPage;
